import { useState, useEffect, useCallback } from "react";
import { jwtDecode } from "jwt-decode";
import DashboardLayout from "../../layouts/DashboardLayout";
import api from "../../api";
import FinanceSummary from "../../components/dashboard/FinanceSummary";
import SubscriptionBilling from "../../components/finance/SubscriptionBilling";
import PlatformFinance from "../../components/finance/PlatformFinance";
import { Wallet, CreditCard } from "lucide-react";

// Mirrors Settings.js's own internal-sidebar-tab pattern (same ?tab= query
// param convention) rather than inventing a new one - Earnings is visible to
// both admin and doctor (self-scoped server-side for a doctor, see
// FinanceSummary), Subscription is admin-only since it's org billing, not a
// doctor's own concern.
// JWT decode is synchronous (just localStorage + a pure decode, no network
// call), so isAdmin is computed directly in useState's initializer rather
// than discovered later inside an effect - resolving it only after the
// first render used to race against the tab-reset guard below: that guard
// would see isAdmin still at its default `false` on the very first commit
// and immediately bounce ?tab=Subscription back to Earnings for an actual
// admin, before setIsAdmin(true) ever had a chance to land.
function resolveIsAdmin() {
  try {
    const storedUser = localStorage.getItem("auth");
    if (storedUser) {
      const authData = JSON.parse(storedUser);
      if (authData.token) {
        const decoded = jwtDecode(authData.token);
        const role = decoded.sub?.role?.toLowerCase() || authData.role?.toLowerCase();
        return ["admin", "ops", "operations"].includes(role);
      }
    }
  } catch (err) {
    console.error("FinanceHome: error decoding token:", err);
  }
  return false;
}

// The platform org (NL Labs) has no doctors and needs no subscription, so it
// gets its own view: subscription revenue, AI cost and profit.
function resolveIsPlatformOrg() {
  try {
    const authData = JSON.parse(localStorage.getItem("auth") || "{}");
    const adminOrgId = process.env.REACT_APP_ADMIN_ORG_ID;
    return Boolean(adminOrgId && authData.token && jwtDecode(authData.token).sub?.org_uid === adminOrgId);
  } catch {
    return false;
  }
}

export default function FinanceHome() {
  const [isPlatformOrg] = useState(resolveIsPlatformOrg);
  const [isAdmin] = useState(resolveIsAdmin);
  const [activeSection, setActiveSection] = useState(() => {
    try {
      return new URLSearchParams(window.location.search).get("tab") || "Earnings";
    } catch {
      return "Earnings";
    }
  });
  const [doctorsList, setDoctorsList] = useState([]);
  const [branchesList, setBranchesList] = useState([]);
  const [selectedDoctorId, setSelectedDoctorId] = useState(null);
  const [selectedBranchId, setSelectedBranchId] = useState(null);

  const fetchDoctorsList = useCallback(async () => {
    try {
      const res = await api.getDoctorsList();
      setDoctorsList(res.data.doctors || []);
    } catch (err) {
      console.error("FinanceHome: error fetching doctors list:", err);
    }
  }, []);

  const fetchBranchesList = useCallback(async () => {
    try {
      const res = await api.fetchOrgBranches();
      setBranchesList(res.data || []);
    } catch (err) {
      console.error("FinanceHome: error fetching branches:", err);
    }
  }, []);

  useEffect(() => {
    if (isAdmin && !isPlatformOrg) {
      fetchDoctorsList();
      fetchBranchesList();
    }
  }, [isAdmin, isPlatformOrg, fetchDoctorsList, fetchBranchesList]);

  useEffect(() => {
    // Subscription is admin-only - if a non-admin somehow lands here with
    // ?tab=Subscription (a stale bookmark, e.g.), fall back to Earnings
    // rather than rendering nothing.
    if (activeSection === "Subscription" && !isAdmin) {
      setActiveSection("Earnings");
    }
  }, [activeSection, isAdmin]);

  const sections = [
    { key: "Earnings", label: "Earnings", subtitle: "Revenue, payouts & transactions", icon: Wallet, visible: true },
    { key: "Subscription", label: "Subscription", subtitle: "Plan, billing & invoices", icon: CreditCard, visible: isAdmin },
  ];

  const adminFilters = isAdmin && (doctorsList.length > 0 || branchesList.length > 0) ? (
    <>
      {branchesList.length > 0 && (
        <select
          className="w-full sm:w-48 px-4 py-2.5 bg-white/50 backdrop-blur-sm border border-secondary-200 rounded-xl focus:ring-2 focus:ring-primary-500/20 focus:border-primary-500 outline-none transition-all cursor-pointer hover:bg-white/80 text-sm"
          onChange={(e) => setSelectedBranchId(e.target.value || null)}
          value={selectedBranchId || ""}
        >
          <option value="">All Branches</option>
          {branchesList.map((b) => (
            <option key={b.id} value={b.id}>{b.name}</option>
          ))}
        </select>
      )}
      {doctorsList.length > 0 && (
        <select
          className="w-full sm:w-56 px-4 py-2.5 bg-white/50 backdrop-blur-sm border border-secondary-200 rounded-xl focus:ring-2 focus:ring-primary-500/20 focus:border-primary-500 outline-none transition-all cursor-pointer hover:bg-white/80 text-sm"
          onChange={(e) => setSelectedDoctorId(e.target.value || null)}
          value={selectedDoctorId || ""}
        >
          <option value="">All Doctors Overview</option>
          {doctorsList.map((doc) => (
            <option key={doc.id} value={doc.id}>
              {doc.name}{doc.email ? ` (${doc.email})` : ""}
            </option>
          ))}
        </select>
      )}
    </>
  ) : null;

  if (isPlatformOrg) {
    return (
      <DashboardLayout>
        <div className="flex flex-col gap-6 animate-fade-in">
          <div>
            <h1 className="text-2xl font-heading font-bold text-secondary-900">Finance</h1>
            <p className="text-sm text-secondary-500 mt-1">What NL Labs earns from clinics, what AI costs, and the patient money held for clinics.</p>
          </div>
          <PlatformFinance />
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="flex flex-col gap-6 animate-fade-in">
        {/* In-page section tabs - a horizontal strip at every width (what the
            old sidebar collapsed into on small screens), so the content
            below gets the full page width. */}
        <div>
          <h1 className="text-2xl font-heading font-bold text-secondary-900 mb-4">Finance</h1>
          <nav className="flex gap-2 overflow-x-auto">
            {sections.filter((s) => s.visible).map((s) => {
              const Icon = s.icon;
              const isActive = activeSection === s.key;
              return (
                <button
                  key={s.key}
                  type="button"
                  onClick={() => setActiveSection(s.key)}
                  className={`flex items-center gap-3 text-left px-4 py-3 rounded-lg text-sm md:text-base font-medium transition-all duration-200 whitespace-nowrap ${isActive
                    ? "bg-primary-600 text-white shadow-md shadow-primary-600/25"
                    : "text-secondary-600 hover:bg-secondary-50 hover:text-secondary-900"
                    }`}
                >
                  <Icon className={`w-5 h-5 ${isActive ? "text-white" : "text-secondary-400"}`} />
                  <div>
                    <div>{s.label}</div>
                    <div className={`text-[11px] ${isActive ? "text-primary-100" : "text-secondary-400"}`}>{s.subtitle}</div>
                  </div>
                </button>
              );
            })}
          </nav>
        </div>

        {/* Active section content */}
        <div className="min-w-0">
          {activeSection === "Earnings" && (
            isAdmin ? (
              <FinanceSummary doctorId={selectedDoctorId} branchId={selectedBranchId} showDoctorColumn filters={adminFilters} />
            ) : (
              <FinanceSummary />
            )
          )}

          {activeSection === "Subscription" && isAdmin && <SubscriptionBilling />}
        </div>
      </div>
    </DashboardLayout>
  );
}
